import styled from "@emotion/styled";
import { Dayjs } from "dayjs";
import type { FC } from "react";
import { getSupportedUserLocale } from "src/lib/i18next";
import translate from "src/lib/translate";
import {
  IconMaskChecked,
  IconMaskCheckedPartner,
  IconMaskMissed
} from "src/constants/icons";
import { WeekEntry } from "./WeightReadingsBloc";

const DISPLAY_DAYS = 9;

const CalendarWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: calc(var(--scale) * 5);
  margin: 1.5rem 0 1.688rem;
  overflow-x: clip;
  overflow-y: visible;
  position: relative;
  --item-width: min(calc(100% - 0.5rem), 2.4rem);

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 1.6rem;
    height: 100%;
    background-image: linear-gradient(
      to right,
      var(--color-cream),
      transparent
    );
    z-index: 2;
  }

  &::after {
    left: auto;
    right: 0;
    background-image: linear-gradient(to left, var(--color-cream), transparent);
  }
`;

const HorizontalCalendar = styled.div`
  display: flex;
  width: calc(calc(100% - 16%) * ${(DISPLAY_DAYS / 7).toFixed(3)});
  margin: 1.6rem 0;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  zip-index: 1;
`;

const DayItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  padding: 0;
  flex: 1;
  font-size: calc(var(--font-size) * 0.9);
`;

const IconSpace = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--item-width);
  aspect-ratio: 1;
  border-radius: 50%;
  background-color: var(--Secondary-Dark-Cream, #f2efe7);
  position: relative;
  overflow: hidden;

  html.partner-session & {
    background-color: var(--partner-10, #ededf5);
  }

  svg {
    --icon-size: calc(90% - 4px);
    width: var(--icon-size);
    height: var(--icon-size);
    position: absolute;
    display: none;

    &.icon-has-reading-partner {
      --icon-size: calc(60% - 4px);
      display: none;
    }
  }

  &[data-has-reading="true"] {
    background:
      linear-gradient(
        to bottom right,
        #ffdfb4 0%,
        #fddee9 51.5625%,
        #c1d7fb 100%
      ),
      linear-gradient(to right, #0e3763 0%, #5b4855 52.6042%, #8a6947 100%);

    .icon-has-reading {
      display: block;
    }

    html.partner-session & {
      background: linear-gradient(
        171deg,
        rgba(43, 38, 165, 0.3) 12.3%,
        rgba(57, 129, 231, 0.3) 81.83%
      );

      .icon-has-reading {
        display: none;
      }

      .icon-has-reading-partner {
        display: block;
      }
    }
  }

  &[data-is-missed="true"] {
    .icon-is-missed {
      display: block;
    }
  }
`;

const DayText = styled.div`
  font-weight: 500;
  position: relative;
  z-index: 2;
  margin-bottom: 0.4rem;
  opacity: 0.5;
  padding: 0 0.1rem;

  &::after {
    content: "";
    position: absolute;
    bottom: 1px;
    height: 2px;
    right: 0;
    left: 0;
    z-index: 1;
  }

  &[data-is-today="true"] {
    opacity: 1;
    background: linear-gradient(to right, #0e3763, #5b4855 52.6%, #8a6947);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    html.partner-session & {
      background: none;
      -webkit-text-fill-color: var(--partner-800, #2c26ac);
      color: var(--partner-800, #2c26ac);
    }

    &::after {
      background: linear-gradient(to right, #0e3763, #5b4855 52.6%, #8a6947);

      html.partner-session & {
        background: var(--partner-800, #2c26ac);
      }
    }
  }
`;

// ─── Daily calendar (original) ───────────────────────────────────────────────

const DailyCalendar: FC<{
  dates: Dayjs[];
  today: Dayjs;
  readings: Dayjs[];
  missed: Dayjs[];
}> = (props) => (
  <CalendarWrap>
    <HorizontalCalendar>
      {props.dates.map((d) => {
        const dayStr = d
          .toDate()
          .toLocaleString(getSupportedUserLocale(), {
            weekday: "short"
          })
          .slice(0, 2);
        const isToday = d.isSame(props.today, "day");
        const hasReading = props.readings.some((r) => r.isSame(d, "day"));
        const isMissed = props.missed.some((r) => r.isSame(d, "day"));

        const dayStringFull = d
          .toDate()
          .toLocaleString(getSupportedUserLocale(), { weekday: "long" });
        const statusTitle = hasReading
          ? translate("dailyReadings.aria.hasReadingDay", {
              weekday: dayStringFull
            })
          : isMissed
            ? translate("dailyReadings.aria.missedDay", {
                weekday: dayStringFull
              })
            : "";

        return (
          <DayItem
            key={d.toString()}
            aria-label={statusTitle}
            title={statusTitle}
          >
            <DayText data-is-today={String(isToday)}>{dayStr}</DayText>
            <IconSpace
              data-has-reading={String(hasReading)}
              data-is-missed={String(isMissed)}
              aria-hidden
            >
              {hasReading && <IconMaskChecked />}
              {hasReading && <IconMaskCheckedPartner />}
              {isMissed && <IconMaskMissed />}
            </IconSpace>
          </DayItem>
        );
      })}
    </HorizontalCalendar>
  </CalendarWrap>
);

// ─── Weekly calendar ──────────────────────────────────────────────────────────

const WeeklyHorizontalCalendar = styled.div<{ weekCount: number }>`
  display: flex;
  width: calc(calc(100% - 16%) * ${(p) => (p.weekCount / 7).toFixed(3)});
  margin: 1.6rem 0;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const WeekIconSpace = styled(IconSpace)`
  overflow: visible;
  cursor: default;

  &[data-has-reading="true"] {
    cursor: pointer;
  }
`;

const TooltipBubble = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-charcoal, #212121);
  color: #fff;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  white-space: nowrap;
  pointer-events: none;
  z-index: 100;
  opacity: 0;
  transition: opacity 0.15s ease;

  *:hover > & {
    opacity: 1;
  }
`;

function getISOWeek(date: Date): number {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const dayOfWeek = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayOfWeek);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

const WeeklyCalendar: FC<{ weeks: WeekEntry[] }> = ({ weeks }) => {
  return (
    <CalendarWrap>
      <WeeklyHorizontalCalendar weekCount={weeks.length}>
        {weeks.map((week) => {
          const weekNum = getISOWeek(week.weekStart.toDate());
          const weekKey = week.weekStart.format("YYYY-MM-DD");
          const canInteract = week.inStreak && !week.isFuture;
          const weekLabel = translate("weeklyStreak.week.label", {
            week: String(weekNum)
          });

          return (
            <DayItem key={weekKey}>
              <DayText data-is-today={String(week.isCurrent)}>
                {weekLabel}
              </DayText>
              <WeekIconSpace
                data-has-reading={String(canInteract)}
                aria-label={weekLabel}
              >
                {canInteract && <IconMaskChecked />}
                {canInteract && <IconMaskCheckedPartner />}
                {canInteract && (
                  <TooltipBubble>{translate("completed")}</TooltipBubble>
                )}
              </WeekIconSpace>
            </DayItem>
          );
        })}
      </WeeklyHorizontalCalendar>
    </CalendarWrap>
  );
};

// ─── Exported component ───────────────────────────────────────────────────────

type DailyCalendarProps = {
  mode: "daily";
  dates: Dayjs[];
  today: Dayjs;
  readings: Dayjs[];
  missed: Dayjs[];
};

type WeeklyCalendarProps = {
  mode: "weekly";
  weeks: WeekEntry[];
};

const CalendarProgress: FC<DailyCalendarProps | WeeklyCalendarProps> = (
  props
) => {
  if (props.mode === "weekly") {
    return <WeeklyCalendar weeks={props.weeks} />;
  }
  return (
    <DailyCalendar
      dates={props.dates}
      today={props.today}
      readings={props.readings}
      missed={props.missed}
    />
  );
};

export default CalendarProgress;
